import React, { useCallback } from 'react';
import {
  ActionIcon,
  Box,
  Drawer,
  Flex,
  Group,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconMap, IconMapOff, IconX } from '@tabler/icons-react';
import { useMobileBreakpoint } from '@repo/utils';

// ─── Responsive breakpoints ──────────────────────────────────────────────────

/** Wide desktop threshold — above this the drawer "pushes" content. */
const WIDE_DESKTOP_QUERY = '(min-width: 87.5em)'; // 1400px

// ─── Types ───────────────────────────────────────────────────────────────────

/** A responsive media drawer with a mini-map toggle slot. */
export interface MediaDrawerProps {
  opened: boolean;
  onClose: () => void;
  title?: string;
  /** Additional controls rendered beside the drawer's close button. */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  /**
   * When provided, renders a mini-map slot inside the drawer header area.
   * Pass a `<MapTarget>` element or any map-like component.
   * The map is toggled via a map icon in the drawer header.
   */
  mapSlot?: React.ReactNode;
  /** Whether the mini-map is visible by default when the drawer opens. */
  mapOpenedDefault?: boolean;
  /** Controls mini-map visibility when provided. */
  mapOpened?: boolean;
  /** Whether to offer a manual mini-map toggle; disable for automatically managed layouts. */
  showMapToggle?: boolean;
  /** Override the responsive overlay, for example to keep the main map interactive. */
  withOverlay?: boolean;
  /** Called when the user toggles the mini-map. */
  onMapOpenedChange?: (opened: boolean) => void;
  /** Callback fired when the drawer's open state changes. Parent layouts
   *  can use this to shrink content when the drawer "pushes" on wide desktop. */
  onOpenChange?: (opened: boolean) => void;
}

/**
 * A responsive media drawer — full-screen bottom sheet on mobile, right-side overlay on desktop — with a mini-map toggle slot.
 */
export function MediaDrawer({
  opened,
  onClose,
  title = 'Media',
  headerActions,
  children,
  mapSlot,
  mapOpenedDefault = false,
  mapOpened,
  showMapToggle = true,
  withOverlay: overlayOverride,
  onMapOpenedChange,
  onOpenChange,
}: MediaDrawerProps) {
  const isMobile = useMobileBreakpoint();
  const isWideDesktop = useMediaQuery(WIDE_DESKTOP_QUERY, false) ?? false;

  const [internalMapExpanded, setInternalMapExpanded] = React.useState(mapOpenedDefault);
  const mapExpanded = mapOpened ?? internalMapExpanded;
  const setMapExpanded = (next: boolean) => {
    if (mapOpened === undefined) setInternalMapExpanded(next);
    onMapOpenedChange?.(next);
  };

  // Notify parent of open-state changes (for push behaviour)
  React.useEffect(() => {
    onOpenChange?.(opened);
  }, [opened, onOpenChange]);

  // Reset map expansion only on the opened -> closed transition. Latest props are read through a
  // ref so changes to mapOpened / callbacks while closed (the parent pre-sets mapOpened before it
  // opens the drawer) never re-trigger the reset.
  const latestMapProps = React.useRef({ mapOpenedDefault, mapOpened, onMapOpenedChange });
  React.useEffect(() => {
    latestMapProps.current = { mapOpenedDefault, mapOpened, onMapOpenedChange };
  });
  const wasOpenedRef = React.useRef(opened);
  React.useEffect(() => {
    const wasOpened = wasOpenedRef.current;
    wasOpenedRef.current = opened;
    if (opened || !wasOpened) return;
    const { mapOpenedDefault: next, mapOpened: controlled, onMapOpenedChange: notify } = latestMapProps.current;
    if (controlled === undefined) setInternalMapExpanded(next);
    notify?.(next);
  }, [opened]);

  React.useEffect(() => {
    if (mapOpened !== undefined) setInternalMapExpanded(mapOpened);
  }, [mapOpened]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // ── Responsive drawer config ───────────────────────────────────────────────

  // Mobile: full-screen bottom sheet
  // Narrow desktop: overlay from right
  // Wide desktop: no overlay, pushes content (parent handles layout shift)
  const position: 'right' | 'bottom' = isMobile ? 'bottom' : 'right';
  const size = isMobile ? '100%' : 420;
  const withOverlay = overlayOverride ?? (isMobile || !isWideDesktop);

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position={position}
      size={size}
      withOverlay={withOverlay}
      overlayProps={withOverlay ? { opacity: 0.35, blur: 2 } : undefined}
      withCloseButton={false}
      padding={0}
      lockScroll={isMobile}
      trapFocus={false}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
      zIndex={250}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Flex
        align="center"
        justify="space-between"
        px="sm"
        py="xs"
        style={{
          flexShrink: 0,
          borderBottom: '1px solid var(--mantine-color-default-border)',
        }}
      >
        <Text fw={600} fz="sm">
          {title}
        </Text>
        <Group gap={4}>
          {headerActions}
          {mapSlot && showMapToggle && (
            <ActionIcon
              variant={mapExpanded ? 'light' : 'subtle'}
              color={mapExpanded ? 'blue' : 'gray'}
              onClick={() => setMapExpanded(!mapExpanded)}
              aria-label={mapExpanded ? 'Hide photo map' : 'Show photo map'}
            >
              {mapExpanded ? <IconMapOff size={18} /> : <IconMap size={18} />}
            </ActionIcon>
          )}
          <ActionIcon variant="subtle" color="gray" onClick={handleClose} aria-label="Close media">
            <IconX size={18} />
          </ActionIcon>
        </Group>
      </Flex>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      {mapSlot && mapExpanded && (
        <Box style={{ height: isMobile ? '42vh' : 280, flexShrink: 0, borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          {mapSlot}
        </Box>
      )}
      <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Box>
    </Drawer>
  );
}
